import React, { useMemo } from 'react'
import { Box, Typography, Paper, Skeleton, CircularProgress } from '@mui/material'
import PieChartIcon from '@mui/icons-material/PieChart'
import BarChartIcon from '@mui/icons-material/BarChart'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart'
import { PlatinumGraphs } from '../platinum-graphs/PlatinumGraphs'
import type { GraphConfig } from '../platinum-graphs/types'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'
import type { ChartDefinition, ChartType } from './chart-types'
import { getFirstDataColumn, getNonBaseRowLabels, getNonBaseColumnLabels, isValidPlatinumData } from './utils'
import { CHART_COLORS, TRANSITIONS } from './design-tokens'

export interface SocialChartProps {
  /** Chart definition with data and rendering config */
  definition: ChartDefinition
  /** PlatinumData to render */
  data: PlatinumData | null
  /** Loading state */
  isLoading?: boolean
  /** Error message */
  error?: string | null
  /** When true, Paper fills its parent container height */
  fillContainer?: boolean
  /** Color map for series colors (key = series name lowercase, value = hex color) */
  colorMap?: Record<string, string>
  /** Gradient map for series colors (key = series name lowercase, value = ECharts gradient object) */
  gradientMap?: Record<string, object>
  /** When true, hides the chart legend */
  hideLegend?: boolean
}

/**
 * Get an icon for a chart type
 */
const getChartIcon = (chartType: ChartType): React.ReactNode => {
  const color = CHART_COLORS[chartType] || CHART_COLORS.bar
  const iconSx = { fontSize: 18, color, opacity: 0.8 }

  switch (chartType) {
    case 'pie':
      return <PieChartIcon sx={iconSx} />
    case 'line':
      return <ShowChartIcon sx={iconSx} />
    case 'stackedBar':
    case 'stackedColumn':
      return <StackedBarChartIcon sx={iconSx} />
    case 'bar':
    case 'column':
    case 'clusteredBar':
    case 'clusteredColumn':
    default:
      return <BarChartIcon sx={iconSx} />
  }
}

// Common card styling for all states
const getCardSx = (fillContainer: boolean, height: number | string) => ({
  p: 3,
  overflow: 'hidden',
  borderRadius: 0,
  borderRight: '1px solid',
  borderBottom: '1px solid',
  borderColor: 'rgba(0,0,0,0.06)',
  bgcolor: 'background.paper',
  transition: TRANSITIONS.card,
  ...(fillContainer && {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  }),
  ...(!fillContainer && {
    height,
  }),
})

/**
 * SocialChart Component
 *
 * Unified chart component that renders any chart type based on a ChartDefinition.
 * Handles loading, error, and empty states consistently with refined styling.
 */
export const SocialChart: React.FC<SocialChartProps> = ({
  definition,
  data,
  isLoading = false,
  error = null,
  fillContainer = false,
  colorMap,
  gradientMap,
  hideLegend = false,
}) => {
  const { title, graph, height = 300 } = definition

  // Resolve selected rows
  // When 'all', pass empty array — PlatinumGraphs defaults to all non-base rows from data.
  const selectedRows = useMemo(() => {
    const config = graph.selectedRows ?? 'all'
    if (config === 'all') {
      return []
    }
    if (config === 'none') {
      return []
    }
    if (Array.isArray(config)) {
      return config
    }
    return []
  }, [graph.selectedRows])

  // Resolve selected columns
  // When 'all', pass empty array — PlatinumGraphs defaults to all non-base columns from data.
  // This avoids stale column labels causing ECharts axis mismatch on re-renders.
  const selectedColumns = useMemo(() => {
    const config = graph.selectedColumns ?? 'all'
    if (config === 'all') {
      return []
    }
    if (config === 'none') {
      return []
    }
    if (Array.isArray(config)) {
      return config
    }
    return []
  }, [graph.selectedColumns])

  // Resolve single selected column (for pie/bar/column charts)
  const selectedColumn = useMemo(() => {
    const config = graph.selectedColumn ?? 'first'
    if (config === 'first' && data) {
      return getFirstDataColumn(data) ?? undefined
    }
    if (config === 'none') {
      return undefined
    }
    return config
  }, [graph.selectedColumn, data])

  // Build the GraphConfig for PlatinumGraphs
  const graphConfig: GraphConfig = useMemo(
    () => ({
      chartType: graph.chartType,
      dataType: graph.dataType,
      selectedRows,
      selectedColumns,
      selectedColumn,
      switchAxis: graph.switchAxis ?? false,
      // ⚠ THIS OBJECT IS BUILT FIELD BY FIELD, NOT SPREAD. A new option on
      // ChartGraphConfig is therefore silently dropped until it is named here
      // AND added to the dependency array below — the flag reads as wired at
      // the definition, and nothing happens (#1201).
      showDataLabels: graph.showDataLabels ?? false,
      hideColumnCaption: graph.hideColumnCaption ?? false,
    }),
    [
      graph.chartType,
      graph.dataType,
      selectedRows,
      selectedColumns,
      selectedColumn,
      graph.switchAxis,
      graph.showDataLabels,
      graph.hideColumnCaption,
    ]
  )

  // Show skeleton while loading with no data
  if (isLoading && !data) {
    return (
      <Paper elevation={0} sx={getCardSx(fillContainer, height)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Skeleton variant="circular" width={18} height={18} />
          <Skeleton variant="text" width="35%" height={24} />
        </Box>
        <Skeleton
          variant="rectangular"
          sx={{
            flexGrow: fillContainer ? 1 : undefined,
            height: fillContainer ? undefined : 'calc(100% - 40px)',
            borderRadius: 1,
          }}
        />
      </Paper>
    )
  }

  // Show error state
  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          ...getCardSx(fillContainer, height),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: 'grey.800',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {getChartIcon(graph.chartType)}
          {title}
        </Typography>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Paper>
    )
  }

  // Show message if no data or invalid data
  if (!isValidPlatinumData(data)) {
    return (
      <Paper
        elevation={0}
        sx={{
          ...getCardSx(fillContainer, height),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: 'grey.800',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {getChartIcon(graph.chartType)}
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isLoading ? 'Loading...' : 'No data available'}
        </Typography>
        {isLoading && <CircularProgress size={24} sx={{ mt: 2 }} />}
      </Paper>
    )
  }

  return (
    <Paper elevation={0} sx={getCardSx(fillContainer, height)}>
      {title && (
        <Typography
          variant="subtitle1"
          sx={{
            mb: 0.5,
            fontWeight: 600,
            color: 'grey.800',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            letterSpacing: '-0.01em',
          }}
        >
          {getChartIcon(graph.chartType)}
          {title}
        </Typography>
      )}
      <Box
        sx={{
          height: fillContainer ? undefined : `calc(100% - ${title ? 28 : 0}px)`,
          flexGrow: fillContainer ? 1 : undefined,
          overflow: 'hidden',
        }}
      >
        <PlatinumGraphs data={data} config={graphConfig} showControls={false} respectParentHeight colorMap={colorMap} gradientMap={gradientMap} hideLegend={hideLegend} />
      </Box>
    </Paper>
  )
}

export default SocialChart
